import permManager, { checkPerm } from 'utils/permManager'
import {
  atLeastCampaignsOnlyPlan,
  atLeastIronscalesProtectPlan,
  atLeastStarterPlan,
} from 'utils/plans'
import {
  canAccessSimulationTrainingSettings,
  hasSimulationTrainingRole,
} from 'utils/simulationTrainingAccess'

import { ADDONS, PLAN_TYPES } from 'containers/App/constants'

/**
 * Compound / cross-source visibility predicates and dynamic href builders for
 * nav items whose gating or destination cannot be expressed by the standard
 * single-source `requires` block (flags / permissions / addOns / integrations
 * under one `entitlementScope`).
 *
 * These are registered by id and referenced from `navConfig.js` via
 * `requires.predicateId` / `href.hrefBuilderId`, keeping the config itself
 * plain serialisable data (for the future CNC editor) while the JS logic lives
 * here. Each predicate reads whichever entitlement source(s) it needs from the
 * resolved entitlement bag (§5.5).
 *
 * The entitlement bag shape (built by `useResolvedNav`):
 * @typedef {object} NavEntitlements
 * @property {object} self    - { permissions, licenses, featureFlags, integrations }
 * @property {object} actual  - { permissions, licenses, featureFlags, integrations }
 * @property {string} region  - top-level state.app.data.region (neither self nor actual)
 * @property {string} businessPillar - top-level state.app.data.business_pillar
 * @property {boolean} partner - whether an MSP/partner is present
 * @property {boolean} hasPsimaCompanies - partner.has_psima_companies (any client has PSIMA)
 * @property {boolean} isStaticSalesDemo
 * @property {boolean} scanBackStatus - actual company scanback ran/active (selectActualScanBackStatus)
 * @property {object|undefined} actualBillableAgent - selectActualBillableAgent(state); the actual
 *   company's agent-enable toggles (e.g. psimaAgent, redAgent). `undefined` for a selected MSP
 *   child tenant (companies_data carries no billableAgent field) — treat as enabled, matching the
 *   backend's missing-row-defaults-to-true semantics; never fall back to the partner's own toggle.
 */

const ADMIN = ['admin']

/** IronTraps roles shared by incidents, investigation, reports, and scan-back. */
const IRONTRAPS_ROLES = ['admin', 'irontraps_action', 'manage_irontraps', 'irontraps_viewer']

export const hasIrontrapsRole = (permissions) => checkPerm(permissions || {}, IRONTRAPS_ROLES)

/**
 * PLGDF (Deepfake product-led-growth promo) — actual + top-level.
 * Mirrors `selectShouldShowPLGDF` (containers/Navigation/selectors.js:133-139):
 *   businessPillar !== 'MSP' && actualIntegrations.o365 === true && !actualFF.DEEPFAKE_SERVICE
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const shouldShowPLGDF = (e) =>
  e.businessPillar !== 'MSP' &&
  e.actual.integrations?.o365 === true &&
  !e.actual.featureFlags?.DEEPFAKE_SERVICE

/**
 * Investigation — plan, kill switch, and IronTraps role.
 * Role and kill switch also fail relevance, so a miss hides instead of locking.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const investigationVisible = (e) =>
  atLeastIronscalesProtectPlan(e.self.licenses?.plan) &&
  !e.self.featureFlags?.DISABLE_INVESTIGATION_TAB &&
  hasIrontrapsRole(e.self.permissions)

/**
 * Deepfake — ENTITLED when the actual company has the deepfake service.
 * (The legacy PLGDF promo case — o365, non-MSP, no service — now renders as a
 * LOCKED section with a PLG click-through instead of a plain link, per the
 * Navigation Menu spec "Deepfake - MS without addon" → lock. Reachability is
 * unchanged: the same tenants see the item, same destination.)
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const deepfakeVisible = (e) => !!e.actual.featureFlags?.DEEPFAKE_SERVICE

/**
 * DMARC entitled — self admin + region prereqs AND the actual company has the
 * DMARC_MANAGEMENT add-on (Navigation Menu spec: "If the customer does not have
 * the add on available it should be marked with a lock sign. Clicking it should
 * open the existing PLG page"). Without the add-on (but admin+region ok) the
 * sub-section renders LOCKED with plg → /dmarc.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const dmarcVisible = (e) =>
  checkPerm(e.self.permissions || {}, ADMIN) &&
  e.region !== 'CA' &&
  !!e.actual.licenses?.add_ons?.includes(ADDONS.DMARC_MANAGEMENT)

/**
 * Email Encryption entitled — self admin AND the actual company
 * has Email Encryption active (Navigation Menu spec: lock sign without the
 * add-on, click opens the existing PLG page; "Office 365 only" — relevance is
 * enforced by NAV_RELEVANCE.EMAIL_ENCRYPTION, GWS-only tenants never see it).
 *
 * Entitlement reads `licenses.email_encryption_settings_visible`, the same field
 * the old nav (components/pages/settings/index.js) and the /encryption PLG page
 * (components/pages/encryptionPLG/index.js) use to decide the very same thing.
 * It must stay that field: the PLG page redirects to the real product
 * (/email-encryption) on exactly this condition, so any other source would
 * unlock a nav item that still lands on the upsell page (or lock one that
 * redirects past it). `add_ons` cannot express it — the backend emits license
 * FIELD names there ('email_encryption_entitled' / 'email_dlp_entitled', see
 * campaigns/models/companylicense.py ADDONS), never 'email_encryption', so the
 * old add-on check was dead and every entitled tenant rendered LOCKED. It also
 * conflates entitled-but-not-activated with active, and misses DLP-only tenants.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const emailEncryptionVisible = (e) =>
  checkPerm(e.self.permissions || {}, ADMIN) &&
  !!e.actual.licenses?.email_encryption_settings_visible

/**
 * Email Encryption product section — the entitlement above AND Office 365. A resolver
 * keeps an entitled node visible even when it is irrelevant, so the o365 gate must live
 * here for the top-level section. The Settings folder keeps the plain predicate: the
 * old nav showed it for any admin with the license, o365 or not.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const emailEncryptionProductVisible = (e) =>
  emailEncryptionVisible(e) && e.actual.integrations?.o365 === true

/**
 * Account Take Over — whole-product entitlement (actual scope).
 * Canonical gate per the ATO dashboard widget (threatProtection/Flow/
 * IncidentOverview/AccountTakeover.js:130): `licenses.ato && integrations.o365`.
 * Without the ato add-on the section renders LOCKED (when relevant — see
 * NAV_RELEVANCE.ATO); GWS-only tenants never see it (hidden).
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const atoVisible = (e) => e.actual.integrations?.o365 === true && !!e.actual.licenses?.ato

/**
 * Accounts investigation for a tenant that has no Account Takeover section to
 * list it (no ATO license, or no Office 365). The page renders its own
 * feature-disabled state, so the entry stays reachable instead of vanishing.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const accountsInvestigationVisible = (e) => investigationVisible(e) && !atoVisible(e)

/**
 * ATO dashboard item - the ATO product gate plus the same IronTraps roles the
 * old Dashboards rail used (HomeDashboardSidebar.js). The API also requires
 * irontraps_viewer, so a role that lacks it reaches the page and gets
 * BlockedState, exactly as it does from the old rail.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const atoDashboardVisible = (e) =>
  atoVisible(e) &&
  checkPerm(e.actual.permissions || {}, [
    'admin',
    'irontraps_action',
    'manage_irontraps',
    'irontraps_viewer',
  ])

/**
 * HRM entitlement: signed-in S&T role and the viewed company's
 * PHISHING_SIMULATION_ALLOWED flag. A role miss is hidden via NAV_RELEVANCE.HRM.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const hrmVisible = (e) =>
  canAccessSimulationTrainingSettings(e.self.permissions, e.actual.featureFlags)

/** Same gate as HRM. Hidden, not locked — the HRM section carries the upsell. */
export const simulationTrainingSettingsVisible = hrmVisible

/**
 * Settings › Activities — self admin AND the actual company is on a paid plan
 * (plan exists && !== STARTER). Mirrors the legacy dropdown gate
 * (`selectUserDropdownItems`, containers/Navigation/selectors.js:47-59):
 *   selfPermissions.admin && actualLicenses.plan && actualLicenses.plan !== STARTER
 * Mixed self+actual scope -> predicate, not standard `requires`.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const activitiesVisible = (e) =>
  checkPerm(e.self.permissions || {}, ADMIN) &&
  !!e.actual.licenses?.plan &&
  e.actual.licenses.plan !== PLAN_TYPES.STARTER

const PSIMA_ROLES = ['admin', 'edit_campaigns']

const hasPsimaRole = (e) => checkPerm(e.self.permissions || {}, PSIMA_ROLES)

const psimaProductEnabled = (e) =>
  !!e.actual.featureFlags?.PSIMA_SERVICE && (e.actualBillableAgent?.psimaAgent ?? true)

/**
 * PS Agent (PSIMA) — same rule as the old-nav gate
 * (`selectSpearPhishingAgentVisibility`): campaign role, then the actual
 * company's FF + psimaAgent toggle, or `hasPsimaCompanies` when an MSP is on
 * their own company (own tenant usually has neither licence nor billed agent).
 * Missing billableAgent on an MSP child defaults enabled; never use the
 * partner's own toggle.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const psimaAgentVisible = (e) =>
  hasPsimaRole(e) && (psimaProductEnabled(e) || !!e.hasPsimaCompanies)

export const settingsSecurityEventForwardingVisible = (e) =>
  !!e.actual.featureFlags?.ENABLE_SEF &&
  !!permManager.pages.settings.integrations.alerts_and_logging(
    e.actual.licenses || {},
    e.actual.permissions || {},
    e.actual.integrations || {}
  )

/**
 * Red Teaming Agent — actual FF + the actual company's redAgent billable toggle.
 * Missing toggle on an MSP child defaults enabled (same as psimaProductEnabled);
 * mirrors the old-nav gate (`selectRedTeamVisibility`).
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const redTeamAgentVisible = (e) =>
  !!e.actual.featureFlags?.ENABLE_RED_TEAM && (e.actualBillableAgent?.redAgent ?? true)

/**
 * SOC Agent dashboard — actual dashboard FF + the actual company's phishSoc
 * billable toggle. Missing toggle on an MSP child defaults enabled (same as
 * psimaProductEnabled). The flag is ENABLE_THEMIS_AI_ANALYSIS_DASHBOARD, not
 * ENABLE_THEMIS_AI_ANALYSIS that the AI Credits gate pairs with phishSoc.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const socAgentVisible = (e) =>
  !!e.actual.featureFlags?.ENABLE_THEMIS_AI_ANALYSIS_DASHBOARD &&
  (e.actualBillableAgent?.phishSoc ?? true)

/**
 * Scan Back page — irontraps perms + starter plan AND the actual company has
 * run/active scanback. Entitlement without scanback used to leave the item
 * visible; the page then redirected away (IC-49684 #3). Scanback status is
 * also the relevance gate, so an unentitled tenant with an active scanback
 * still locks rather than disappearing.
 * Vendor Base - actual permissions, plan and the VENDOR_BASE flag.
 * Calls permManager's own rule rather than restating it, so the menu item and
 * the page itself cannot disagree about who may see the feature. Read off the
 * actual company, which is the source the page uses too.
 *
 * @param {NavEntitlements} e
 * @returns {boolean}
 */
export const scanBackVisible = (e) =>
  checkPerm(e.self.permissions || {}, [
    'admin',
    'irontraps_action',
    'manage_irontraps',
    'irontraps_viewer',
  ]) &&
  atLeastStarterPlan(e.self.licenses?.plan) &&
  !!e.scanBackStatus
export const vendorProtectionVisible = (e) =>
  !!permManager.pages.vendorProtection.self(
    e.actual.licenses || {},
    e.actual.permissions || {},
    e.actual.integrations || {},
    e.actual.featureFlags || {}
  )

/**
 * Registry of named visibility predicates, keyed by `predicateId`.
 */
export const NAV_PREDICATES = {
  PLGDF: shouldShowPLGDF,
  INVESTIGATION: investigationVisible,
  DEEPFAKE: deepfakeVisible,
  DMARC: dmarcVisible,
  EMAIL_ENCRYPTION: emailEncryptionVisible,
  EMAIL_ENCRYPTION_PRODUCT: emailEncryptionProductVisible,
  ATO: atoVisible,
  ACCOUNTS_INVESTIGATION: accountsInvestigationVisible,
  ATO_DASHBOARD: atoDashboardVisible,
  HRM: hrmVisible,
  SETTINGS_SIMULATION_TRAINING: simulationTrainingSettingsVisible,
  ACTIVITIES: activitiesVisible,
  PSIMA_AGENT: psimaAgentVisible,
  SETTINGS_SEF: settingsSecurityEventForwardingVisible,
  RED_TEAM_AGENT: redTeamAgentVisible,
  SOC_AGENT: socAgentVisible,
  SCANBACK: scanBackVisible,
  VENDOR_PROTECTION: vendorProtectionVisible,
}

/**
 * RELEVANCE predicates, keyed by `relevanceId` (spec "Lock menu items"):
 * a capability is locked ONLY when it is relevant to the customer's integration
 * type / prerequisites; an unentitled-AND-irrelevant capability is HIDDEN.
 * A node with no `relevanceId` is always relevant (locked whenever unentitled).
 *
 * - ATO: tied to Office 365 — GWS-only tenants never see the section.
 * - DEEPFAKE: MS-only product promo; matches legacy PLGDF reach
 *   (o365 + non-MSP pillar). Entitled tenants see it regardless via the
 *   entitlement predicate.
 * - EMAIL_ENCRYPTION: "Office 365 only" + the legacy admin prereq (no region condition).
 * - DMARC: legacy admin/region prereqs ("check pre requisits") + O365 only —
 *   crosswalk marks DMARC "Non-O365 (e.g. GWS): not shown", same as Encryption.
 * - SCANBACK: scanback ran/active for the actual company (the page redirects away
 *   otherwise); crosswalk 2157576211 lists "FF" — relevance = scanback status.
 */
export const NAV_RELEVANCE = {
  ATO: (e) => e.actual.integrations?.o365 === true,
  DEEPFAKE: (e) => e.actual.integrations?.o365 === true && e.businessPillar !== 'MSP',
  EMAIL_ENCRYPTION: (e) =>
    checkPerm(e.self.permissions || {}, ADMIN) && e.actual.integrations?.o365 === true,
  DMARC: (e) =>
    checkPerm(e.self.permissions || {}, ADMIN) &&
    e.region !== 'CA' &&
    e.actual.integrations?.o365 === true,
  SCANBACK: (e) => !!e.scanBackStatus && hasIrontrapsRole(e.self.permissions),
  // Role miss hides. Plan miss stays relevant and locks via `requires`.
  IRONTRAPS: (e) => hasIrontrapsRole(e.self.permissions),
  // Kill switch hides Investigation. It is not an upsell.
  INVESTIGATION: (e) =>
    hasIrontrapsRole(e.self.permissions) && !e.self.featureFlags?.DISABLE_INVESTIGATION_TAB,
  ACCOUNTS_INVESTIGATION: (e) => NAV_RELEVANCE.INVESTIGATION(e) && !atoVisible(e),
  // Only admin / campaign editor see the HRM upsell.
  HRM: (e) => hasSimulationTrainingRole(e.self.permissions),
}

/**
 * DMARC destination — self-visibility / actual-destination scope mix (§5.6).
 * Mirrors index.js:104-107, 252-254:
 *   isAdmin = actualPermissions.admin
 *   isDmarcManagementEnabled = actualLicenses.add_ons.includes(DMARC_MANAGEMENT)
 *   isDmarcEnabled = isAdmin && isDmarcManagementEnabled
 *   when enabled -> magic-link `/members-api/.../?companyFilter=${actualCompanyId}` (new tab)
 *   else         -> in-app `/dmarc`
 *
 * @param {NavEntitlements} e
 * @param {{ inAppPath: string, companyId: (string|number) }} ctx
 * @returns {{ path: string, external: boolean, newTab: boolean }}
 */
export const buildDmarcHref = (e, ctx) => {
  const isAdmin = !!e.actual.permissions?.admin
  const isDmarcManagementEnabled = !!e.actual.licenses?.add_ons?.includes(ADDONS.DMARC_MANAGEMENT)
  const isDmarcEnabled = isAdmin && isDmarcManagementEnabled
  if (isDmarcEnabled) {
    return {
      path: `/members-api/power-dmarc/magic-link-login-redirect/?companyFilter=${ctx.companyId}`,
      external: true,
      newTab: true,
    }
  }
  return { path: ctx.inAppPath, external: false, newTab: false }
}

/**
 * Registry of named dynamic-href builders, keyed by `hrefBuilderId`.
 */
export const NAV_HREF_BUILDERS = {
  DMARC: buildDmarcHref,
}

// Re-exported helpers for the parity tests (lets the test reproduce standard
// single-source rules without re-importing from utils).
export { checkPerm, atLeastCampaignsOnlyPlan, atLeastIronscalesProtectPlan, atLeastStarterPlan }
